import React, { useState, useRef, useEffect } from 'react';
import { ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react';
import ReactDOMServer from 'react-dom/server';
import { InfoCard, InfoCardStack as IInfoCardStack } from '../types/cards.types';
import analyticsService from '../services/analyticsService';
import { safeGetDomain } from '../utils/urlUtils';
import { useInfoCardAudio, useInfoCards } from '../hooks/queries/useInfoCards';
import { useLanguage } from '../lib/i18n';
import BackgroundIcon from './common/BackgroundIcon';
import AudioPlayer from './AudioPlayer';
import MarkdownRenderer from './MarkdownRenderer';

interface Props {
  stack: IInfoCardStack;
  isExpanded: boolean;
  onToggle: (stackId: string, height: number) => void;
  iconIndex?: number;
}

const InfoCardStack = ({ stack, isExpanded, onToggle, iconIndex = 0 }: Props) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState<'none' | 'left' | 'right'>('none');
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [maxContentHeight, setMaxContentHeight] = useState<number>(0);
  const [contentHeights, setContentHeights] = useState<number[]>([]);
  const isManualNavigation = useRef<boolean>(false);
  const [prevItem, setPrevItem] = useState<InfoCard | null | undefined>(null);
  const [prevIndex, setPrevIndex] = useState<number | null>(null);
  const [isAudioMounted, setIsAudioMounted] = useState<boolean>(false);
  const audioPlayerRef = useRef<{
    seekTo: (time: number) => void;
  } | null>(null);

  const { language } = useLanguage();
  const { data: cards } = useInfoCards(language, stack.id);
  const { data: audio } = useInfoCardAudio(language, stack.id);

  // Add check for audio availability
  const hasAudio = !!audio && !!audio.url;

  // Effect to sync audio position when audio player mounts or content index changes
  useEffect(() => {
    if (isAudioMounted && hasAudio && audioPlayerRef.current && audio?.timings[currentIndex]) {
      isManualNavigation.current = true;
      audioPlayerRef.current.seekTo(audio?.timings[currentIndex]?.startTime ?? 0);
      setTimeout(() => {
        isManualNavigation.current = false;
      }, 50);
    }
  }, [isAudioMounted, currentIndex, audio?.timings, hasAudio]);

  useEffect(() => {
    if (!cards) return;

    if (contentRef.current) {
      // Measure heights of all content items
      const tempDiv = document.createElement('div');
      tempDiv.style.visibility = 'hidden';
      tempDiv.style.position = 'absolute';
      tempDiv.style.width = contentRef.current.offsetWidth + 'px';
      tempDiv.className = contentRef.current.className;

      const heights = cards.map((card) => {
        tempDiv.innerHTML = '';
        const contentElement = document.createElement('div');
        contentElement.innerHTML = ReactDOMServer.renderToString(
          <MarkdownRenderer markdown={processContent(card.text)} />
        );
        tempDiv.appendChild(contentElement);
        document.body.appendChild(tempDiv);
        const height = tempDiv.scrollHeight;
        document.body.removeChild(tempDiv);
        return height;
      });

      setContentHeights(heights);

      // Calculate total height with proper padding
      const baseHeight = Math.max(...heights);
      const audioHeight = audio && audio.url ? 80 : 0; // Height of audio player
      const navigationHeight = 45; // Height of navigation buttons
      const contentPadding = 80; // Padding for content area
      setMaxContentHeight(baseHeight + audioHeight + navigationHeight + contentPadding);
    }
  }, [cards, audio]);

  const processContent = (text: string) => {
    // Convert URLs to markdown links with domain names
    const urlRegex = /(https:\/\/\S+)/g;
    return text.replace(urlRegex, (url) => {
      const domain = safeGetDomain(url);
      return `[${domain} {{external-link}}](${url})`;
    });
  };

  const handleClick = () => {
    // Track event when opening the card
    if (!isExpanded) {
      analyticsService.trackEvent({
        eventName: 'informationCardOpened',
        properties: {
          cardTitle: stack.title,
          pageUrl: window.location.href,
        },
      });
    }
    onToggle(stack.id, maxContentHeight);
  };

  const handleSlideComplete = () => {
    setIsAnimating(false);
    setPrevItem(null);
    setPrevIndex(null);
    setSlideDirection('none');
  };

  const getCurrentContentHeight = () => {
    if (!isAnimating) return contentHeights[currentIndex] || 0;
    const prevHeight = contentHeights[prevIndex ?? 0] || 0;
    const currentHeight = contentHeights[currentIndex] || 0;
    return Math.max(prevHeight, currentHeight);
  };

  const handleNavigation = (direction: 'previous' | 'next') => {
    if (isAnimating) return;

    const isPrevious = direction === 'previous';
    const eventName = isPrevious ? 'leftArrowClicked' : 'rightArrowClicked';
    const newSlideDirection = isPrevious ? 'right' : 'left';
    const newIndex = isPrevious
      ? currentIndex > 0
        ? currentIndex - 1
        : cards!.length - 1
      : currentIndex < cards!.length - 1
        ? currentIndex + 1
        : 0;

    analyticsService.trackEvent({
      eventName: eventName,
      properties: { cardTitle: stack.title },
    }); // Tracking event
    setIsAnimating(true);
    setPrevItem(cards![currentIndex]);
    setPrevIndex(currentIndex);
    setSlideDirection(newSlideDirection);
    setCurrentIndex(newIndex);

    if (hasAudio && isExpanded) {
      isManualNavigation.current = true;
      const timing = audio?.timings[newIndex];
      audioPlayerRef.current?.seekTo?.(timing?.startTime ?? 0);
      setTimeout(() => {
        isManualNavigation.current = false;
      }, 50);
    }
  };

  const handlePrevious = () => handleNavigation('previous');
  const handleNext = () => handleNavigation('next');

  const currentItem = cards?.[currentIndex];

  return (
    <div className="w-full overflow-hidden rounded border bg-card text-card-foreground">
      <div
        className="relative flex cursor-pointer items-center bg-muted px-4 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset"
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleClick();
          }
        }}
      >
        <div className="flex min-w-0 flex-1 items-center justify-center">
          <span className="text-center font-button">{stack.title}</span>
        </div>
        <div className="absolute right-2">
          <ChevronUp
            className={`transform transition-transform ${isExpanded ? '' : 'rotate-180'}`}
            style={{ transitionDuration: '500ms' }}
          />
        </div>
      </div>
      <div
        ref={contentRef}
        className="relative overflow-hidden transition-all duration-500 ease-in-out"
        style={{
          height: isExpanded ? `${maxContentHeight}px` : '0px',
          opacity: isExpanded ? 1 : 0,
        }}
      >
        {hasAudio && isExpanded && audio && (
          <div className="transition-all duration-500 ease-in-out">
            <AudioPlayer
              ref={audioPlayerRef}
              url={audio.url}
              onMount={() => setIsAudioMounted(true)}
              onUnmount={() => setIsAudioMounted(false)}
              onTimeUpdate={(time) => {
                if (!isManualNavigation.current) {
                  const newIndex =
                    audio?.timings.findIndex(
                      (timing) => time >= timing.startTime && time < timing.endTime
                    ) ?? 0;
                  if (newIndex !== -1 && newIndex !== currentIndex) {
                    setPrevItem(cards?.[currentIndex]);
                    setPrevIndex(currentIndex);
                    setSlideDirection(newIndex > currentIndex ? 'left' : 'right');
                    setIsAnimating(true);
                    setCurrentIndex(newIndex);
                  }
                }
              }}
            />
          </div>
        )}
        <div className="relative border-t">
          <div className="relative overflow-hidden px-4 pt-4">
            <div className="mb-4">
              <p className="text-sm text-muted-foreground">
                {currentIndex + 1}/{cards?.length}
              </p>
            </div>
            <div
              className="relative"
              style={{
                minHeight: '80px',
                height: `${getCurrentContentHeight()}px`,
              }}
            >
              {prevItem && (
                <div
                  className={`absolute inset-0 ${
                    slideDirection === 'left' ? 'animate-slide-left' : 'animate-slide-right'
                  }`}
                  onAnimationEnd={handleSlideComplete}
                >
                  <div className="whitespace-normal break-words text-sm">
                    <MarkdownRenderer markdown={processContent(prevItem.text)} />
                  </div>
                </div>
              )}
              <div
                className={`absolute inset-0 ${
                  isAnimating
                    ? slideDirection === 'left'
                      ? 'animate-slide-in-right'
                      : 'animate-slide-in-left'
                    : ''
                }`}
              >
                <div className="whitespace-normal break-words text-sm">
                  <MarkdownRenderer markdown={processContent(currentItem?.text ?? '')} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <BackgroundIcon iconIndex={iconIndex} />
        <div className="absolute bottom-0 left-0 right-0 flex z-2">
          <button
            className="flex flex-1 items-center justify-center border-r border-t bg-card p-3"
            onClick={handlePrevious}
            disabled={isAnimating}
          >
            <ChevronLeft />
          </button>
          <button
            className="flex flex-1 items-center justify-center border-t bg-card p-3"
            onClick={handleNext}
            disabled={isAnimating}
          >
            <ChevronRight />
          </button>
        </div>
      </div>
    </div>
  );
};

export default InfoCardStack;
